import { useState, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { exportData, importData } from '../services/storage';
import { 
  Save,
  Loader2,
  CheckCircle,
  User,
  Truck,
  Mail,
  Download,
  Upload,
  AlertCircle,
  Database
} from 'lucide-react';

export default function Settings() {
  const { settings, saveSettings, isLoading } = useSettings();
  
  const [driverName, setDriverName] = useState(settings.driverName);
  const [truckNumber, setTruckNumber] = useState(settings.truckNumber);
  const [driverId, setDriverId] = useState(settings.driverId);
  const [bennettEmail, setBennettEmail] = useState(settings.bennettEmail);
  const [cpaEmail, setCpaEmail] = useState(settings.cpaEmail);
  const [reminderDay, setReminderDay] = useState(settings.reminderDay);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [importStatus, setImportStatus] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    setDriverName(settings.driverName);
    setTruckNumber(settings.truckNumber);
    setDriverId(settings.driverId);
    setBennettEmail(settings.bennettEmail);
    setCpaEmail(settings.cpaEmail);
    setReminderDay(settings.reminderDay);
  }, [settings]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSaved(false);

    try {
      await saveSettings({
        driverName,
        truckNumber,
        driverId,
        bennettEmail,
        cpaEmail,
        reminderDay,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Failed to save settings');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const data = await exportData();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tax-tracker-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export data');
      console.error(err);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const result = await importData(text);
      setImportStatus({
        success: true,
        message: `Imported ${result.imported} expenses${result.errors > 0 ? ` (${result.errors} errors)` : ''}`,
      });
      setTimeout(() => setImportStatus(null), 5000);
    } catch (err) {
      setImportStatus({
        success: false,
        message: 'Failed to import data. Make sure the file is a valid backup.',
      });
      console.error(err);
    }

    // Reset the input
    e.target.value = '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-white/60 mt-1">Configure your driver info and email templates</p>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {saved && (
        <div className="flex items-center space-x-2 p-4 bg-green-500/20 border border-green-500/50 rounded-xl text-green-300 text-sm animate-fade-in">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>Settings saved successfully!</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Driver Info */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3 mb-4">
            <User className="w-5 h-5 text-shuttle-sand" />
            <h2 className="text-lg font-semibold text-white">Driver Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="driverName" className="block text-sm text-white/60 mb-1">
                Driver Name
              </label>
              <input
                id="driverName"
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="truckNumber" className="block text-sm text-white/60 mb-1">
                  <Truck className="w-4 h-4 inline mr-1" />
                  Truck Number
                </label>
                <input
                  id="truckNumber"
                  type="text"
                  value={truckNumber}
                  onChange={(e) => setTruckNumber(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all font-mono"
                />
              </div>
              <div>
                <label htmlFor="driverId" className="block text-sm text-white/60 mb-1">
                  Driver ID
                </label>
                <input
                  id="driverId"
                  type="text"
                  value={driverId}
                  onChange={(e) => setDriverId(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all font-mono"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Email Settings */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3 mb-4">
            <Mail className="w-5 h-5 text-shuttle-sand" />
            <h2 className="text-lg font-semibold text-white">Email Settings</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="bennettEmail" className="block text-sm text-white/60 mb-1">
                Bennett Imaging Email
              </label>
              <input
                id="bennettEmail"
                type="email"
                value={bennettEmail}
                onChange={(e) => setBennettEmail(e.target.value)}
                placeholder="imaging@bennett..."
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
              <p className="text-xs text-white/40 mt-1">Used for BOL email template</p>
            </div>

            <div>
              <label htmlFor="cpaEmail" className="block text-sm text-white/60 mb-1">
                CPA Email (Optional)
              </label>
              <input
                id="cpaEmail"
                type="email"
                value={cpaEmail}
                onChange={(e) => setCpaEmail(e.target.value)}
                placeholder="your-cpa@atbs.com"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>

            <div>
              <label htmlFor="reminderDay" className="block text-sm text-white/60 mb-1">
                CPA Reminder Day
              </label>
              <select
                id="reminderDay"
                value={reminderDay}
                onChange={(e) => setReminderDay(parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>
                    {day}{day === 7 ? ' (Recommended - day before 8th)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-white/40 mt-1">Get reminded to export docs for CPA</p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          type="submit"
          disabled={isSaving}
          className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Save Settings</span>
            </>
          )}
        </button>
      </form>

      {/* Data Management */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center space-x-3 mb-4">
          <Database className="w-5 h-5 text-shuttle-sand" />
          <h2 className="text-lg font-semibold text-white">Data Management</h2>
        </div>

        {importStatus && (
          <div className={`flex items-center space-x-2 p-4 rounded-xl text-sm mb-4 ${
            importStatus.success 
              ? 'bg-green-500/20 border border-green-500/50 text-green-300'
              : 'bg-red-500/20 border border-red-500/50 text-red-300'
          }`}>
            {importStatus.success ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span>{importStatus.message}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleExport}
            className="flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
          >
            <Download className="w-5 h-5" />
            <span>Export Backup</span>
          </button>

          <label className="flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-colors cursor-pointer">
            <Upload className="w-5 h-5" />
            <span>Import Backup</span>
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
          </label>
        </div>

        <p className="text-xs text-white/40 mt-3 text-center">
          💡 Regularly backup your data to avoid losing your expense records
        </p>
      </div>
    </div>
  );
}






